import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { ImageUploaderProps, PreviewImage } from "./types";
import { useImageLoader } from "./hooks/useImageLoader";
import { useFileUpload } from "./hooks/useFileUpload";
import { cleanupUrls } from "./utils";
import { createPreviewUrl } from "tools/functions/Methods";
import { UploadZone } from "./components/uploadZone";
import FilePreview from "./components/file-preview";
import { GrDocumentMissing } from "react-icons/gr";

const MultiImageUploader: FC<ImageUploaderProps> = ({
  onImagesUploaded,
  onImageUploaded,
  maxImages = 5,
  currentImages = [],
  currentImage,
  acceptedFormats = ["image/png", "image/jpeg", "image/gif", "application/pdf",".xlsx"],
  mode = "single",
  preview = false,
  renderExtraInfo,
}) => {



  const effectiveMax = mode === "single" ? 1 : maxImages;
  const imageIds =
    mode === "single" ? (currentImage ? [currentImage] : []) : currentImages;

  const loadedPreviews = useImageLoader(imageIds, mode);
  const [previews, setPreviews] = useState<PreviewImage[]>([]);

  useEffect(() => {
    setPreviews(loadedPreviews);
  }, [loadedPreviews]);

  useEffect(() => () => cleanupUrls(previews), []);

  const handleComplete = (ids: string[]) => {
    if (mode === "single" && ids[0]) {
      onImageUploaded?.(ids[0]);
    } else if (mode === "multiple") {
      onImagesUploaded?.(ids);
    }
  };

  const { uploadFile, uploadingFiles } = useFileUpload(handleComplete, mode);

  const handleFiles = async (files: FileList) => {
    const remaining = effectiveMax - previews.length;
    if (remaining <= 0) {
      toast.error(
        mode === "single"
          ? "فقط یک فایل مجاز است"
          : `حداکثر ${effectiveMax} فایل`
      );
      return;
    }

    const validFiles = Array.from(files)
      .slice(0, remaining)
      .filter((f) => acceptedFormats.includes(f.type));

    if (!validFiles.length) {
      toast.error("فرمت فایل‌ها معتبر نیست");
      return;
    }

    if (mode === "single" && previews.length) {
      cleanupUrls(previews);
      setPreviews([]);
    }

    const newPreviews = validFiles.map((f) => ({
      url: createPreviewUrl(f),
      name: f.name,
      type: f.type,
    }));

    const startIdx = mode === "single" ? 0 : previews.length;
    setPreviews(
      mode === "single" ? newPreviews : [...previews, ...newPreviews]
    );

    for (let i = 0; i < validFiles.length; i++) {
      await uploadFile(validFiles[i], startIdx + i, setPreviews);
    }
  };

  const handleRemove = (index: number) => {
    cleanupUrls([previews[index]]);
    const newPreviews = previews.filter((_, i) => i !== index);
    setPreviews(newPreviews);
    const ids = newPreviews.map((p) => p.id).filter((id): id is string => !!id);
    handleComplete(ids);
    onImagesUploaded?.(ids);
  };

  return (
    <div className="w-full">
      {previews.length > 0 ? (
        <div className={preview ? "" : "mb-4"}>
          <div
            className={`grid gap-3 ${
              mode === "single"
                ? "grid-cols-1"
                : "grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
            }`}
          >
           {previews.map((p, idx) => (
            <FilePreview
              key={p.id || idx}
              preview={p}
              index={idx}
              isUploading={uploadingFiles.has(idx)}
              onRemove={handleRemove}
              readOnly={!onImagesUploaded}
              // تنها تغییر: فراخوانی تابع برای گرفتن محتوای اضافی بر اساس آیدی تصویر
              infoContent={renderExtraInfo ? renderExtraInfo(p.id) : undefined} 
            />
          ))}
          </div>
        </div>
      ) : preview ? (
        <div className=" w-[15rem] h-32 flex flex-col items-center justify-center rounded-lg border border-gray-300 bg-gray-50 text-center p-3">
          <div className="flex items-center justify-center bg-gray-200 text-gray-500 w-12 h-12 rounded-full shadow-sm mb-2">
            <GrDocumentMissing size={28} />
          </div>
          <p className="text-gray-600 text-sm font-medium">
            هیچ فایلی یافت نشد
          </p>
        </div>
      ) : (
        ""
      )}

      {!preview && previews.length < effectiveMax && (
        <UploadZone
          onFilesSelected={handleFiles}
          acceptedFormats={acceptedFormats}
          remainingSlots={effectiveMax - previews.length}
          mode={mode}
        />
      )}
    </div>
  );
};

export default MultiImageUploader;
